import { Order } from "../../ws/types";
import { Button } from "../ui";
import "../../styles/order/OrderCard.scss";

interface Props {
    order: Order;
    rating?: {
        tier: number;
        completed_deals: number;
        success_rate: number;
    };
    isMyOrder: boolean;
    isQAMode: boolean;
    qaProfile?: string;
    onTake: (orderId: string) => void;
    onCancel: (orderId: string) => void;
    isCancelling: boolean;
}

export default function OrderCard({
    order,
    rating,
    isMyOrder,
    isQAMode,
    qaProfile,
    onTake,
    onCancel,
    isCancelling
}: Props) {
    const filledAmount = Number(order.filled_amount ?? 0);
    const remainingAmount = Number(order.amount ?? 0);
    const originalAmount = Number(order.original_amount ?? (filledAmount > 0 ? filledAmount + remainingAmount : 0));
    const hasProgress = filledAmount > 0 && originalAmount > 0;
    const fillPercentage = hasProgress
        ? Math.min(100, (filledAmount / originalAmount) * 100)
        : 0;

    return (
        <div className={`order-card ${order.side}`}>
            <div className="order-header">
                <span className={`badge badge-${order.side}`}>
                    {order.side === "buy" ? "Покупка" : "Продажа"}
                </span>
                <div className="payment-methods">
                    {order.payment_methods.map(method => (
                        <span key={method} className="payment-badge">
                            {method}
                        </span>
                    ))}
                </div>
                {isQAMode && (
                    <span className="qa-tag">QA</span>
                )}
            </div>

            <div className="order-body">
                <div className="amount-row">
                    <span className="amount">{order.amount}</span>
                    <span className="token-symbol"> {order.jetton_symbol}</span>
                </div>

                {/* Progress indicator for partially filled orders */}
                {hasProgress && (
                    <div className="fill-progress">
                        <div className="progress-bar-mini">
                            <div 
                                className="progress-fill-mini" 
                                style={{ width: `${fillPercentage}%` }}
                            />
                        </div>
                        <span className="fill-text">
                            Выполнено: {filledAmount.toFixed(2)} / {originalAmount.toFixed(2)} 
                            ({fillPercentage.toFixed(0)}%)
                        </span>
                    </div>
                )}

                <div className="price-row">
                    <span className="price">@ {order.price} {order.fiat_currency}</span>
                </div>
                <div className="total-row">
                    <span className="total">Итого: </span>
                    <span className="total-amount">
                        <strong>{order.fiat_amount.toFixed(2)}</strong> {order.fiat_currency}
                    </span>
                </div>
                {rating && (
                    <div className="user-rating">
                        <span className="rating-stars">
                            {"⭐".repeat(rating.tier)}
                        </span>
                        <span className="rating-details">
                            {rating.completed_deals} сделок · {rating.success_rate}% успех
                        </span>
                    </div>
                )}
            </div>

            <div className="order-footer">
                {isMyOrder ? (
                    <div className="my-order-actions">
                        <span className="my-order-badge">Ваш ордер</span>
                        <Button
                            variant="danger"
                            size="sm"
                            loading={isCancelling}
                            onClick={() => onCancel(order.id)}
                        >
                            Отменить
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="primary"
                        block
                        onClick={() => onTake(order.id)}
                    >
                        {isQAMode && qaProfile === 'buyer' ? '📤 Создать заявку' : 'Взять ордер'}
                    </Button>
                )}
            </div>
        </div>
    );
}
